// src/features/system/devices/components/iot/ShareViewDialog.tsx
// ────────────────────────────────────────────────────────────────────────────
// Share Telemetry View — Dialog for creating parameterised shareable URLs.
//
// Lets users customise per-device labels for the shared view, optionally
// name the view, and generates a persistent shareable URL via the BFF.
// Generates entity + hot-stream audit trail server-side.
//
// Click → clipboard → green "copied" transition with visual feedback.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import AppDialog from '@/shared/components/ui/AppDialog';
import {
  createViewLinkApi,
  type Device,
  type ViewLinkDevice,
} from '../../api/iotDevicesApi';

/* ── Props ────────────────────────────────────────────────────────────── */

interface ShareViewDialogProps {
  open:          boolean;
  onOpenChange:  (v: boolean) => void;
  devices:       Device[];
  deviceLabel:   (dev: Device) => string;
  timeWindow:    string;     // e.g. '1h'
  palette:       string[];
}

/* ── Helpers ──────────────────────────────────────────────────────────── */

/** Copy text to clipboard with textarea fallback for older browsers. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

/* ── Component ────────────────────────────────────────────────────────── */

export default function ShareViewDialog({
  open, onOpenChange, devices, deviceLabel, timeWindow, palette,
}: ShareViewDialogProps) {

  // Per-device label overrides — initialised from current device labels
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [viewLabel, setViewLabel] = useState('');
  const [copied, setCopied]       = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      const initial: Record<string, string> = {};
      devices.forEach(d => { initial[d.device_id] = deviceLabel(d); });
      setOverrides(initial);
      setViewLabel('');
      setCopied(false);
      setGeneratedUrl(null);
    }
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, [open, devices, deviceLabel]);

  const setOverride = useCallback((deviceId: string, label: string) => {
    setOverrides(prev => ({ ...prev, [deviceId]: label }));
  }, []);

  // ── Create mutation ────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: createViewLinkApi,
    onSuccess: async (data) => {
      const fullUrl = `${window.location.origin}${data.url_path}`;
      setGeneratedUrl(fullUrl);
      const ok = await copyToClipboard(fullUrl);
      if (ok) {
        setCopied(true);
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = setTimeout(() => setCopied(false), 3000);
      }
    },
  });

  const handleGenerate = useCallback(() => {
    const viewDevices: ViewLinkDevice[] = devices.map(d => ({
      device_id: d.device_id,
      label:     (overrides[d.device_id] ?? '').trim() || null,
    }));
    createMutation.mutate({
      devices:     viewDevices,
      label:       viewLabel.trim() || undefined,
      time_window: timeWindow,
    });
  }, [devices, overrides, viewLabel, timeWindow, createMutation]);

  const handleCopyAgain = useCallback(async () => {
    if (!generatedUrl) return;
    const ok = await copyToClipboard(generatedUrl);
    if (ok) {
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 3000);
    }
  }, [generatedUrl]);

  const isBusy = createMutation.isPending;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Share Telemetry View"
      description={`Generate a shareable URL for ${devices.length} device${devices.length > 1 ? 's' : ''} · ${timeWindow} window`}
      size="lg"
      isBusy={isBusy}
      footer={
        <div className="flex items-center gap-3 w-full">
          {/* Error */}
          {createMutation.isError && (
            <span className="text-xs text-rose-400 mr-auto">
              {(createMutation.error as Error)?.message || 'Failed to create link'}
            </span>
          )}

          <div className="ml-auto flex gap-3">
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
            >
              Cancel
            </button>

            {!generatedUrl ? (
              <button
                onClick={handleGenerate}
                disabled={isBusy || devices.length === 0}
                className="px-5 py-2 text-sm font-medium rounded-md transition-all
                  bg-emerald-600/80 hover:bg-emerald-600 text-white
                  disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isBusy ? 'Generating…' : 'Generate & Copy'}
              </button>
            ) : (
              <button
                onClick={handleCopyAgain}
                className={`px-5 py-2 text-sm font-medium rounded-md transition-all duration-300
                  ${copied
                    ? 'bg-emerald-500/90 text-white shadow-[0_0_12px_rgba(52,211,153,0.3)]'
                    : 'bg-white/[0.08] text-neutral-200 hover:bg-white/[0.12]'}`}
              >
                {copied ? '✓ Copied!' : 'Copy again'}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* ── View label (optional) ─────────────────────────────────── */}
      <div>
        <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-1.5">
          View name <span className="text-neutral-600">(optional)</span>
        </label>
        <input
          type="text"
          value={viewLabel}
          onChange={e => setViewLabel(e.target.value)}
          placeholder="e.g. Office sensors — weekly"
          maxLength={128}
          className="w-full bg-black border border-neutral-700 rounded px-3 py-2 text-sm text-neutral-200
            placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </div>

      {/* ── Device label overrides ────────────────────────────────── */}
      <div>
        <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">
          Device labels for this link
        </label>
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
          {devices.map((dev, idx) => (
            <div key={dev.device_id} className="flex items-center gap-3">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: palette[idx % palette.length] }}
              />
              <span className="text-xs text-neutral-500 font-mono w-20 truncate shrink-0" title={dev.device_id}>
                {dev.device_id.length > 10 ? `…${dev.device_id.slice(-8)}` : dev.device_id}
              </span>
              <input
                type="text"
                value={overrides[dev.device_id] ?? ''}
                onChange={e => setOverride(dev.device_id, e.target.value)}
                placeholder={deviceLabel(dev)}
                maxLength={64}
                className="flex-1 bg-black border border-neutral-700 rounded px-2.5 py-1.5 text-sm text-neutral-200
                  placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Generated URL result ──────────────────────────────────── */}
      {generatedUrl && (
        <div
          className={`rounded-md border px-4 py-3 transition-all duration-500
            ${copied
              ? 'border-emerald-500/40 bg-emerald-500/[0.06] shadow-[0_0_16px_rgba(52,211,153,0.08)]'
              : 'border-white/[0.08] bg-white/[0.02]'}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium transition-colors duration-300
              ${copied ? 'text-emerald-400' : 'text-neutral-400'}`}>
              {copied ? '✓ Copied to clipboard' : 'Shareable URL'}
            </span>
          </div>
          <code className="text-xs text-neutral-300 break-all select-all leading-relaxed">
            {generatedUrl}
          </code>
        </div>
      )}
    </AppDialog>
  );
}
